import React, { useState, useEffect, useRef, forwardRef } from "react";

const LazyImage = forwardRef(({ src, alt, selected, onClick, ...props }, ref) => {
    // Define state for the image source and visibility
    const [imageSrc, setImageSrc] = useState("");
    const [isVisible, setIsVisible] = useState(false);

    // Define a ref for the image element
    const imageRef = useRef(null);

    // Run the intersection observer when the component mounts
    useEffect(() => {
        // If a ref is provided, set the image as visible
        if (ref) {
            setIsVisible(true);
        } else {
            // Otherwise, create an intersection observer to detect when the image is visible
            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            setIsVisible(true);
                            observer.unobserve(imageRef.current);
                        }
                    });
                },
                { rootMargin: "0px 0px 150px 0px", threshold: 0.25 }
            );

            // Observe the image element
            if (imageRef.current) {
                observer.observe(imageRef.current);
            }

            // Clean up the observer when the component unmounts
            return () => {
                if (imageRef.current) {
                    observer.unobserve(imageRef.current);
                }
            };
        }
    }, [ref]);

    // Set the image source when it becomes visible
    useEffect(() => {
        if (isVisible) {
            setImageSrc(src);
        }
    }, [isVisible, src]);

    // Generate CSS class names based on the selected state
    const classNames = ["lazy-image"];
    if (selected) {
        classNames.push("collage-selected");
    }

    // Render the image element
    return (
        <img
            className={classNames.join(" ")}
            ref={ref || imageRef}
            src={imageSrc}
            alt={alt}
            onClick={onClick}
            crossOrigin="anonymous"
            {...props}
        />
    );
});

export default LazyImage;
